import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';

import { ModelPDP, Product } from '../types';
import { getImagesList, ProductImageList, productImageWidths } from '../utils';
import { useContainerSize } from '../../common/hooks/useContainerSize';
import { useMediaQueries } from '../../common/hooks/useMediaQuery';
import { ModelSizes } from './ModelSizes';
import { durations, zIndex } from '../../common/constants';
import { breakpoints, colors, Icon, opacities } from '../../../design-system';
import useBodyScrollLock from '../../common/hooks/useBodyScrollLock';

type Props = {
  imagePositions: number[];
  productRef: string;
  colorRef: string;
  productName: string;
  colorLabel: string;
  product: Product;
  videoUrl?: string;
  model?: ModelPDP[] | null;
};

const StyledImagesMozaic = styled.div<{ height: number }>`
  display: grid;
  width: 100%;
  grid-gap: 8px;
  grid-template-columns: 1fr 1fr;
  position: relative;

  .image-btn-container {
    background: none;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    position: relative;
    transition: opacity ${durations.ENTER}ms ease-in-out;

    @media (hover: hover) {
      :hover {
        cursor: zoom-in;
        opacity: ${opacities.HOVERED};
      }
    }

    img {
      object-fit: cover;
      width: calc(100% - 4px);
      border-radius: 8px;
    }
  }

  .iframe-container {
    width: 100%;
    height: ${(props) => props.height}px;
  }

  .blur-up {
    -webkit-filter: blur(5px);
    filter: blur(5px);
    transition:
      filter 200ms,
      -webkit-filter 200ms;
  }

  .blur-up.lazyloaded {
    -webkit-filter: blur(0);
    filter: blur(0);
  }
`;

const StyledImageMozaicZoomWrapper = styled.div<{ btnsPosition: number }>`
  position: fixed;
  z-index: ${zIndex.MODAL};
  top: 0;
  left: ${({ btnsPosition }) => btnsPosition}px;
  width: 100vw;
  max-width: ${breakpoints.XL}px;
  height: 100vh;
  margin: auto;
  overflow-y: auto;

  .zoom-image {
    display: block;
    width: 100%;
    height: auto;
    max-height: none;
  }

  .zoom-image-close-btn,
  .zoom-image-left-btn,
  .zoom-image-right-btn {
    position: fixed;
    z-index: ${zIndex.MODAL + 1};
    padding: 0;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${colors.WHITE};
    transition: opacity ${durations.ENTER}ms ease-in-out;

    &::before {
      content: '';
      position: absolute;
      top: -6px;
      bottom: -6px;
      left: -6px;
      right: -6px;
      background: transparent;
    }

    @media (hover: hover) {
      :hover {
        cursor: pointer;
        opacity: ${opacities.HOVERED};
      }
    }
  }

  .zoom-image-close-btn {
    top: 32px;
    right: ${({ btnsPosition }) => btnsPosition + 32}px;
  }

  .zoom-image-left-btn,
  .zoom-image-right-btn {
    transform: translateY(-50%);
    top: 50%;
  }

  .zoom-image-left-btn {
    left: ${({ btnsPosition }) => btnsPosition + 32}px;
  }

  .zoom-image-right-btn {
    right: ${({ btnsPosition }) => btnsPosition + 32}px;
  }

  .zoom-image-dots-wrapper {
    position: fixed;
    display: flex;
    gap: 8px;
    z-index: ${zIndex.MODAL + 1};
    bottom: 32px;
    left: 50%;
    transform: translateX(-50%);
  }

  .zoom-image-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${colors.WHITE};
  }

  .zoom-image-dot-active {
    background-color: ${colors.BLACK};
  }

  .image-btn-wrapper {
    padding: 0;
    border: none;
    background: transparent;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${zIndex.MODAL};

    @media (hover: hover) {
      :hover {
        cursor: zoom-out;
      }
    }
  }

  .blur-up {
    -webkit-filter: blur(5px);
    filter: blur(5px);
    transition:
      filter 200ms,
      -webkit-filter 200ms;
  }

  .blur-up.lazyloaded {
    -webkit-filter: blur(0);
    filter: blur(0);
  }
`;

export function ImagesMozaic({
  productRef,
  colorRef,
  productName,
  colorLabel,
  videoUrl,
  model = null,
  imagePositions = [],
}: Props) {
  const { isMobile, isTablet, isDesktop } = useMediaQueries();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const [imagesQuality, setImageQuality] = useState<ProductImageList>([]);
  const [btnsPosition, setBtnsPosition] = useState(0);
  const imageRef = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock({ isActive: showZoom, excludeId: 'image-mozaic-zoom' });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const container = useContainerSize(containerRef, isMobile, isTablet, isDesktop);
  const images = getImagesList({
    imagePositions,
    productRef,
    productName,
    colorRef,
    colorLabel,
    width: productImageWidths.MOSAIC,
  });

  const onClickRight = () => {
    if (activeIndex < images.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const onClickLeft = () => {
    if (activeIndex !== 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const zoomContent = (
    <StyledImageMozaicZoomWrapper id="image-mozaic-zoom" btnsPosition={btnsPosition} ref={imageRef}>
      <button
        data-testid="zoom-image-desktop-close-btn"
        type="button"
        onClick={() => setShowZoom(false)}
        className="zoom-image-close-btn"
      >
        <Icon name="close" color={colors.BLACK} />
      </button>
      {activeIndex > 0 && (
        <button
          data-testid={`zoom-image-mozaic-go-to-previous-picture-index-${activeIndex - 1}`}
          type="button"
          onClick={onClickLeft}
          className="zoom-image-left-btn"
        >
          <Icon name="arrowLeft" color={colors.BLACK} />
        </button>
      )}
      {activeIndex < images.length - 1 && (
        <button
          data-testid={`zoom-image-mozaic-go-to-next-picture-index-${activeIndex + 1}`}
          type="button"
          onClick={onClickRight}
          className="zoom-image-right-btn"
        >
          <Icon name="arrowRight" color={colors.BLACK} />
        </button>
      )}
      {imagesQuality?.length > 1 && (
        <div className="zoom-image-dots-wrapper">
          {imagesQuality.map((image, index) => {
            return (
              <div
                key={image.default.jpg}
                className={`zoom-image-dot ${activeIndex === index ? 'zoom-image-dot-active' : ''}`}
              />
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={() => setShowZoom(false)}
        data-testid="image-zoom-close-btn-wrapper"
        className="image-btn-wrapper"
        style={{ position: 'relative' }}
      >
        <picture>
          <source
            srcSet={images?.[activeIndex]?.default.webp}
            data-srcSet={imagesQuality?.[activeIndex]?.default.webp}
            type="image/webp"
          />
          <img
            src={images?.[activeIndex]?.default.jpg}
            srcSet={`${imagesQuality?.[activeIndex]?.default.jpg} 2x`}
            data-srcSet={imagesQuality?.[activeIndex]?.default.jpg}
            alt={imagesQuality?.[activeIndex]?.default.alt}
            className="zoom-image blur-up lazyload"
          />
        </picture>
      </button>
    </StyledImageMozaicZoomWrapper>
  );

  const onImageClick = (index: number) => {
    setActiveIndex(index);
    setShowZoom(true);
  };

  useEffect(() => {
    const getImagesQualityAndBtnsPosition = () => {
      const imagesWithHighDefinition = getImagesList({
        imagePositions,
        productRef,
        productName,
        colorRef,
        colorLabel,
        width: window.innerWidth > 1920 ? 3840 : window.innerWidth * 2,
      });
      setImageQuality(imagesWithHighDefinition);
      setBtnsPosition(
        window.innerWidth > breakpoints.XL ? (window.innerWidth - breakpoints.XL) / 2 : 0
      );
    };

    getImagesQualityAndBtnsPosition();

    window.addEventListener('resize', getImagesQualityAndBtnsPosition);

    return () => {
      window.removeEventListener('resize', getImagesQualityAndBtnsPosition);
    };
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowZoom(false);
      }
    };

    if (showZoom) {
      document.addEventListener('keydown', handleKey);
    } else {
      document.removeEventListener('keydown', handleKey);
    }

    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [showZoom]);

  useEffect(() => {
    if (imageRef?.current) {
      imageRef.current.scrollTop = 0;
    }
  }, [activeIndex]);

  return (
    <StyledImagesMozaic ref={containerRef} height={(((container.width - 8) / 2) * 4) / 3}>
      {images.map((image, index) => {
        return (
          <button
            type="button"
            data-testid="image-mozaic-desktop-click-to-zoom"
            onClick={() => onImageClick(index)}
            className="image-btn-container"
            key={`${image.thumbnail.jpg}-${index}`}
          >
            <picture>
              <source
                srcSet={image.thumbnail.webp}
                data-srcset={image.default.webp}
                type="image/webp"
              />
              <img
                src={image.thumbnail.jpg}
                srcSet={`${image.default.jpg} 2x`}
                data-srcSet={image.default.jpg}
                alt={image.default.alt}
                className="blur-up lazyload"
              />
            </picture>
            {model && (imagePositions?.length ?? 0) > 0 && (
              <ModelSizes imagePositions={imagePositions} model={model} index={index} />
            )}
          </button>
        );
      })}
      {videoUrl && (
        <div className="iframe-container">
          <iframe
            data-src={videoUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="lazyload"
          />
        </div>
      )}
      {showZoom && ReactDOM.createPortal(zoomContent, document.body)}
    </StyledImagesMozaic>
  );
}
